import React from 'react';
import { BsArrowRepeat } from 'react-icons/bs';
import SearchableSelect from './SearchableSelect';
import StatusBadge from './StatusBadge';
import { ENTITY_STATUS_API_OPTIONS } from '../../utils/status';
import type { ConfirmDetailRow } from './DeleteConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';

export type StatusUpdateDialogProps = {
  open: boolean;
  title: string;
  entityLabel: string;
  entityName: string;
  currentStatus: unknown;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  details?: ConfirmDetailRow[];
  statusOptions?: { value: string; label: string }[];
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

const StatusUpdateDialog: React.FC<StatusUpdateDialogProps> = ({
  open,
  title,
  entityLabel,
  entityName,
  currentStatus,
  selectedStatus,
  onStatusChange,
  details = [],
  statusOptions = ENTITY_STATUS_API_OPTIONS,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}) => {
  const unchanged =
    selectedStatus.trim().toUpperCase() ===
    String(currentStatus ?? '').trim().toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BsArrowRepeat className="h-5 w-5 text-primary" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription>
            Update the status for {entityLabel} <strong>{entityName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
          {details.map((row) => (
            <div key={row.label} className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">{row.label}</span>
              <span className="text-right font-medium">{row.value}</span>
            </div>
          ))}
          <div className="flex justify-between gap-4 text-sm">
            <span className="text-muted-foreground">Current Status</span>
            <span className="text-right font-medium">
              <StatusBadge status={currentStatus} />
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="status-update-select">New Status</Label>
          <SearchableSelect
            id="status-update-select"
            value={selectedStatus}
            options={statusOptions}
            onChange={onStatusChange}
            placeholder="Select status"
            searchPlaceholder="Search status…"
            emptyMessage="No status options available."
            disabled={loading}
            menuPortal={false}
            aria-label="New status"
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={loading || unchanged}
          >
            {loading ? 'Saving…' : 'Update Status'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatusUpdateDialog;
